import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { Location } from 'src/models/location/_types';
import { fetchLocations } from 'src/models/location/location';
import { Identifier } from 'src/types/utilityTypes';

interface LocationSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onLocationSelect: (location: Location) => void;
  title?: string;
  allowClose?: boolean;
  currentLocationId?: string;
  onNoLocationsAvailable?: () => void;
}

export const LocationSelectionDialog: FC<LocationSelectionDialogProps> = ({
  open,
  onClose,
  onLocationSelect,
  title,
  allowClose = true,
  currentLocationId,
  onNoLocationsAvailable,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'locationSelection',
  });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  // Fetch locations for location selection dialog
  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: () => fetchLocations({ fetchAll: true }),
    enabled: open, // Only fetch when dialog is open
  });

  // Handle special cases based on location count
  useEffect(() => {
    if (!locationsLoading && locations && open) {
      const availableLocations = locations.locations || [];

      // If no locations exist, notify parent that location selection is not required
      if (availableLocations.length === 0 && onNoLocationsAvailable) {
        onNoLocationsAvailable();
        return;
      }

      // If only one location exists, auto-select it
      if (availableLocations.length === 1) {
        onLocationSelect(availableLocations[0]);
        return;
      }
    }
  }, [
    locationsLoading,
    locations,
    open,
    onNoLocationsAvailable,
    onLocationSelect,
  ]);

  const handleLocationSelect = (location: Location) => {
    onLocationSelect(location);
  };

  const handleClose = () => {
    if (allowClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={title || t('selectLocation')}
      subtitle={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('whySelectLocationText')}
          </Typography>
          <Tooltip
            title={tCommon('tooltips.whySelectLocation')}
            placement="top"
          >
            <Icon
              icon={faCircleQuestion}
              color="primary"
              size="small"
              sx={{ cursor: 'help' }}
            />
          </Tooltip>
        </Box>
      }
      maxWidth="sm"
      fullWidth
    >
      {locationsLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>{t('loadingLocations')}</Typography>
        </Box>
      ) : locations?.locations && locations.locations.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {t('noLocationsAvailable')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {locations?.locations?.map((location) => {
            const isCurrentLocation =
              currentLocationId === location.id.toString();
            return (
              <ListItem
                key={location.id}
                primary={location.name}
                secondary={location.address}
                onClick={() => handleLocationSelect(location)}
                clickable
                sx={{
                  borderRadius: 1,
                  backgroundColor: isCurrentLocation
                    ? 'primary.main'
                    : 'transparent',
                  color: isCurrentLocation ? 'primary.contrastText' : 'inherit',
                  '&:hover': {
                    backgroundColor: isCurrentLocation
                      ? 'primary.dark'
                      : 'action.hover',
                  },
                }}
              />
            );
          })}
        </Stack>
      )}
    </Dialog>
  );
};

// Hook for using location selection functionality
export const useLocationSelection = () => {
  const handleLocationSelect = (
    location: Location,
    updateLocationId?: (locationId: Identifier) => void,
    onLocationSelected?: (location: Location) => void
  ) => {
    if (updateLocationId) {
      updateLocationId(location.id);
    }
    if (onLocationSelected) {
      onLocationSelected(location);
    }
  };

  return { handleLocationSelect };
};
